import { Box, FormControl, FormHelperText, MenuItem, Select, Stack, Typography, useMediaQuery } from '@mui/material'
import { FunctionComponent, ReactNode, SetStateAction } from 'react'
import gaiaTheme from '../../../../root-components/theme';
import { TooltipButton } from '../../miscellaneous-components/tooltip-button';
import FormEditButtons from './form-edit-buttons';

export const FormSelectInputDisplay: FunctionComponent<{
  isEditPage: boolean,
  allowEdit?: boolean,
  editField?: string | undefined,
  setEditField?: React.Dispatch<SetStateAction<any>> | undefined,
  hideEditIcon?: boolean,
  disabled: boolean,
  isLastField?: boolean,
  size?: 'small' | 'medium',
  title: string,
  name: string,
  value: any,
  touched?: any,
  errors?: any,
  onChange: any,
  cancelEditHandler?: () => void,
  array: any,
  valueKey?: string,
  labelKey?: string | ((item: any) => string),
  additionalText?: string,
  showAdditionalText?: boolean,
  customErrorMessage?: string,
  tooltip?: string,
  bottomBorder?: boolean,
  topBorder?: boolean,
  sideBorders?: boolean,
  isTopRow?: boolean,
  isBottomRow?: boolean,
  forceOutlined?: boolean,
  // Optional inline action(s) rendered to the right of the select, on the same row.
  endActions?: ReactNode,
  // Render the matched option label when the value is '' (MUI hides it by default). Needed by
  // fields whose empty value is a meaningful, labelled choice (e.g. "Use subsidiary default").
  displayEmpty?: boolean,
}> = ({
  isEditPage,
  allowEdit = true,
  editField,
  setEditField,
  hideEditIcon = false,
  disabled,
  isLastField = false,
  size = 'small',
  title,
  name,
  value,
  touched,
  errors,
  onChange,
  cancelEditHandler,
  array,
  valueKey,
  labelKey,
  additionalText,
  showAdditionalText = false,
  customErrorMessage,
  tooltip,
  bottomBorder = false,
  topBorder = true,
  sideBorders = true,
  isTopRow = false,
  isBottomRow = false,
  forceOutlined = false,
  endActions,
  displayEmpty = false,
}) => {

    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    const toggleEditField = (fieldName: string) => {
      if (setEditField) {
        setEditField(fieldName)
      }
    }

    return (
      <FormControl fullWidth variant='outlined'>
        <Stack sx={{
          borderWidth: '1px',
          borderBottomStyle: bottomBorder || isBottomRow ? 'solid' : 'none',
          borderColor: 'lightGrey.main',
          borderLeftStyle: sideBorders ? 'solid' : 'none',
          borderRightStyle: sideBorders ? 'solid' : 'none',
          borderTopStyle: topBorder || isTopRow ? 'solid' : 'none',
          pt: 1, pb: 1, pl: 3,
          borderTopLeftRadius: isTopRow ? '10px' : 0,
          borderTopRightRadius: isTopRow ? '10px' : 0,
          borderBottomLeftRadius: isBottomRow ? '10px' : 0,
          borderBottomRightRadius: isBottomRow ? '10px' : 0
        }}>
          {showAdditionalText && <Typography variant='body2' color='text.secondary'>{additionalText}</Typography>}
          <Stack position='relative' alignItems={isMobileMode ? 'flex-start' : 'center'} flexDirection={isMobileMode ? 'column' : 'row'} justifyContent='start'>
            <Stack sx={{ width: isMobileMode ? '100%' : '40%', minWidth: '100px', color: 'text.primary', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Typography variant='subtitle1'> {title} </Typography>
              {tooltip && !isMobileMode && <TooltipButton
                text={tooltip}
                noPadding={true}
                placement='bottom' />}
            </Stack>
            <Stack position='relative' flexDirection='column' sx={{ width: '100%' }}>
              <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'>
                <Select
                  id={name} name={name}
                  value={value}
                  disabled={disabled}
                  onChange={onChange}
                  error={touched && Boolean(errors)}
                  size={size}
                  displayEmpty={displayEmpty}
                  fullWidth={!endActions}
                  sx={{
                    mt: 1, mb: 1, mr: 15, ml: 1,
                    ...((!forceOutlined && (editField !== name || isEditPage)) && {
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }),
                    ...(((forceOutlined) || editField === name || !isEditPage) && {
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondary' }
                    }),
                    ...(!isEditPage && {
                      mt: 1, mb: 1, mr: 2, ml: 2,
                    }),
                    // When an inline action sits alongside, the select is a flex item that shrinks
                    // to share the row (fullWidth would overflow and overlap the action).
                    ...(endActions && {
                      flex: 1, minWidth: 0, mr: 0,
                    }),
                    color: 'text.primary',
                  }}>
                  {array.map((item: any, index: number) => (
                    <MenuItem key={index}
                      value={valueKey ? item[valueKey] : item}>
                      {labelKey ? (typeof labelKey === 'function' ? labelKey(item) : item[labelKey]) : item.charAt(0).toUpperCase() + item.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
                {endActions &&
                  <Box sx={{ flexShrink: 0, ml: 2, mr: 3, display: 'flex', alignItems: 'center' }}>
                    {endActions}
                  </Box>}
                {allowEdit &&
                  <FormEditButtons
                    isEditPage={isEditPage}
                    editField={editField}
                    hideEditIcon={hideEditIcon}
                    name={name}
                    toggleEditField={toggleEditField}
                    cancelEditHandler={cancelEditHandler}
                  />}
              </Stack>
              {touched && errors &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {errors ? <div>{errors as string}</div> : null}
                </FormHelperText>
              }
              {customErrorMessage &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{customErrorMessage}
                </FormHelperText>
              }
            </Stack>
          </Stack>
        </Stack>
      </FormControl>
    )
  }